import { FormProvider, getFormProps } from '@conform-to/react'
import { Button, Flex } from '@radix-ui/themes'
import { useLocation, useParams } from '@remix-run/react'
import * as React from 'react'
import { RxPencil1 } from 'react-icons/rx'
import { type Params } from 'react-router-dom'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SelectCountryCode } from '#app/components/common/select-country-code.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import { TextField } from '#app/components/ui/text-field.tsx'
import { type GetAddress } from '#app/models/ecommerce-providers/sylius/shop/fetch/address/get-address.ts'
import { type Locale } from '#app/models/locale.ts'
import { type FormEnum } from '#app/routes/_layout/route-config.ts'
import { generateItemFormFetcherKey } from '#app/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '#app/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useEditAddressForm } from '#app/utils/hooks/forms/use-edit-address-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Edit address form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	firstName = 'firstName',
	lastName = 'lastName',
	phoneNumber = 'phoneNumber',
	company = 'company',
	street = 'street',
	city = 'city',
	postcode = 'postcode',
	countryCode = 'countryCode',
	defaultAddress = 'defaultAddress',
}

const FORM: FormEnum = {
	generateId: ({ id }) => generateItemFormId('edit-address-form', id),
	generateAction: (params: Params) => `/profile/addresses/${params.id}`,
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('edit-address', id),
	subaction: 'edit-address',
	redirect: '/profile/addresses',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.firstName]: z
			.string({ required_error: 'validation.firstName.required' })
			.min(2, 'validation.firstName.min')
			.max(255, 'validation.firstName.max'),
		[INPUTS.lastName]: z
			.string({ required_error: 'validation.lastName.required' })
			.min(2, 'validation.lastName.min')
			.max(255, 'validation.lastName.max'),
		[INPUTS.phoneNumber]: z
			.string()
			.min(2, 'validation.phoneNumber.min')
			.max(255, 'validation.phoneNumber.max')
			.optional(),
		[INPUTS.company]: z
			.string()
			.min(2, 'validation.company.min')
			.max(255, 'validation.company.max')
			.optional(),
		[INPUTS.street]: z
			.string({ required_error: 'validation.street.required' })
			.min(2, 'validation.street.min')
			.max(255, 'validation.street.max'),
		[INPUTS.city]: z
			.string({ required_error: 'validation.city.required' })
			.min(2, 'validation.city.min')
			.max(255, 'validation.city.max'),
		[INPUTS.postcode]: z
			.string({ required_error: 'validation.postcode.required' })
			.min(2, 'validation.postcode.min')
			.max(255, 'validation.postcode.max'),
		[INPUTS.countryCode]: z.string({
			required_error: 'validation.countryCode.required',
		}),
		[INPUTS.defaultAddress]: z.boolean().optional(),
	},
})

type EditAddressFormProps = {
	address: GetAddress
	locale: Locale
}

const EditAddressForm: React.FC<EditAddressFormProps> = ({
	address,
	locale,
}) => {
	const {
		editAddressFetcher,
		editAddressFormValues,
		editAddressForm,
		editAddressFields,
		t,
	} = useEditAddressForm({ address })

	const location = useLocation()
	const params = useParams()

	return (
		<FormProvider context={editAddressForm.context}>
			<editAddressFetcher.Form
				method="post"
				action={`/${locale}${FORM.generateAction!(params)}${location.search}`}
				{...getFormProps(editAddressForm)}
			>
				<FormDefaults
					form={editAddressForm}
					fields={editAddressFields}
					values={editAddressFormValues}
				/>
				<Flex direction="column" gapY="5" mt="2">
					{[
						{ title: INPUTS.firstName },
						{ title: INPUTS.lastName },
						{ title: INPUTS.phoneNumber, type: 'tel' },
						{ title: INPUTS.company },
						{ title: INPUTS.street },
						{ title: INPUTS.city },
						{ title: INPUTS.postcode, type: 'tel' },
					].map(({ title, type = 'text' }) => (
						<TextField
							key={title}
							type={type}
							meta={editAddressFields[title]}
							t={t}
						>
							{t(`label.${title}`)}
						</TextField>
					))}
					<SelectCountryCode
						meta={editAddressFields[INPUTS.countryCode]}
						t={t}
						placeholder={t('label.countryCode')}
					>
						{t('label.countryCode')}
					</SelectCountryCode>
					<Checkbox t={t} meta={editAddressFields[INPUTS.defaultAddress]}>
						{t('button.setAsDefaultAddress')}
					</Checkbox>
					<Button size="4" type="submit">
						<SubmissionButton fetcher={editAddressFetcher}>
							<RxPencil1 size="1.2em" />
							{t('button.editAddressSubmit')}
						</SubmissionButton>
					</Button>
				</Flex>
			</editAddressFetcher.Form>
		</FormProvider>
	)
}
EditAddressForm.displayName = 'EditAddressForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type EditAddressFormProps,
	EditAddressForm,
	INPUTS as EDIT_ADDRESS_INPUTS,
	FORM as EDIT_ADDRESS_FORM,
	schema as editAddressSchema,
}
